/**
 * フォームビルダーの型定義
 */

import { Type, AlignLeft, Hash, Calendar, CheckSquare, ChevronDown, Clock, CalendarClock, Circle, CheckSquare as CheckSquareMulti } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

export type BaseFormField = {
  id: string
  name: string
  label: string
  description?: string
  required?: boolean
}

export type TextFormField = BaseFormField & {
  type: 'text'
  placeholder?: string
  minLength?: number
  maxLength?: number
}

export type TextareaFormField = BaseFormField & {
  type: 'textarea'
  placeholder?: string
  minLength?: number
  maxLength?: number
}

export type NumberFormField = BaseFormField & {
  type: 'number'
  placeholder?: string
  minimum?: number
  maximum?: number
}

export type DateFormField = BaseFormField & {
  type: 'date'
}

export type CheckboxFormField = BaseFormField & {
  type: 'checkbox'
}

export type SelectFormField = BaseFormField & {
  type: 'select'
  options: Array<{ label: string; value: string }>
}

export type TimeFormField = BaseFormField & {
  type: 'time'
  defaultValue?: string
  minTime?: string  // HH:mm format
  maxTime?: string  // HH:mm format
  stepMinutes?: number
}

export type DateTimeFormField = BaseFormField & {
  type: 'datetime'
  defaultValue?: string
  minDateTime?: string  // ISO 8601 format
  maxDateTime?: string  // ISO 8601 format
}

export type RadioFormField = BaseFormField & {
  type: 'radio'
  options: Array<{ label: string; value: string }>
  defaultValue?: string
}

export type MultiSelectFormField = BaseFormField & {
  type: 'multiselect'
  options: Array<{ label: string; value: string }>
  defaultValue?: string[]
}

export type FormField =
  | TextFormField
  | TextareaFormField
  | NumberFormField
  | DateFormField
  | CheckboxFormField
  | SelectFormField
  | TimeFormField
  | DateTimeFormField
  | RadioFormField
  | MultiSelectFormField

export type BuilderState = {
  fields: FormField[]
}

export type FieldType = FormField['type']

export const FIELD_TYPES: Array<{ type: FieldType; label: string; description: string; icon: LucideIcon }> = [
  // テキスト入力系
  { type: 'text', label: 'テキスト入力', description: '1行のテキスト入力フィールド', icon: Type },
  { type: 'textarea', label: 'テキストエリア', description: '複数行のテキスト入力フィールド', icon: AlignLeft },
  { type: 'number', label: '数値入力', description: '数値専用の入力フィールド', icon: Hash },
  // 選択系
  { type: 'select', label: 'ドロップダウン', description: '選択肢から1つを選択', icon: ChevronDown },
  { type: 'radio', label: 'ラジオボタン', description: '選択肢から1つを選択（ラジオボタン）', icon: Circle },
  // 真偽値・複数選択系
  { type: 'checkbox', label: 'チェックボックス', description: 'オン/オフの選択', icon: CheckSquare },
  { type: 'multiselect', label: 'チェックボックス複数選択', description: '複数の選択肢を選択可能', icon: CheckSquareMulti },
  // 日時系
  { type: 'date', label: '日付選択', description: '日付ピッカー', icon: Calendar },
  { type: 'time', label: '時刻入力', description: '時刻入力（HH:mm）', icon: Clock },
  { type: 'datetime', label: '日時入力', description: '日付と時刻の入力', icon: CalendarClock },
]
